const THEME_BP_TABLET = 768;
const THEME_BP_DESKTOP = 1080;

/** True when the device reports touch (tablets/laptops with touch); avoids gating swipe on viewport width alone. */
function isTouchCapable() {
  if (typeof navigator !== 'undefined' && Number(navigator.maxTouchPoints) > 0) return true;
  if (typeof window !== 'undefined' && 'ontouchstart' in window) return true;
  return false;
}

/** Max rAF retries when `thumbsSelector` points at a partner `<slider-component>` not yet initialized. */
const MAX_THUMBS_PARTNER_RETRIES = 48;

/**
 * Defaults when `<script data-slider-config>` is missing or invalid.
 * All sliders should include JSON; these are a safe fallback.
 */
const SLIDER_CONFIG_DEFAULTS = {
  enabled: true,
  desktopOnly: false,
  sectionId: '',
  slidesPerViewMobile: '1.5',
  slidesPerViewTablet: '',
  slidesPerViewDesktop: '3',
  spaceBetweenMobile: 15,
  spaceBetweenDesktop: 15,
  direction: 'horizontal',
  loop: false,
  speed: 500,
  zoom: false,
  centeredSlidesMobile: false,
  centeredSlidesDesktop: false,
  allowTouchMoveDesktop: false,
  slideToClickedSlide: false,
  centeredSlidesBounds: false,
  paginationType: 'bullets',
  showPagination: true,
  showNavigation: true,
  showScrollbar: false,
  autoplayMobile: false,
  autoplaySpeedMobile: 5000,
  autoplayDesktop: false,
  autoplaySpeedDesktop: 5000,
  disableDesktop: false,
  enablePositionControls: false,
  thumbsSelector: '',
  watchOverflow: false,
  /**
   * When non-null, overrides drag/touch behavior for `allowTouchMove` (including on touch devices).
   * @type {boolean | null}
   */
  allowTouchMoveOverride: null,
  /**
   * When true, `allowTouchMove` is false only at the desktop Swiper breakpoint; mobile/tablet use normal rules.
   */
  disableTouchDragDesktop: false,
  /** @type {number | null} Starting slide index; overrides centeredSlides-derived default when finite */
  initialSlide: null,
  /** @type {number | null} Desktop breakpoint initial slide; falls back to initialSlide then centeredSlidesDesktop */
  initialSlideDesktop: null,
};

/**
 * @param {string | undefined} value
 * @param {number} fallback
 */
function parseIntData(value, fallback) {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {boolean} forDesktop
 */
function pickInitialSlideIndex(cfg, forDesktop) {
  const desktopRaw = cfg.initialSlideDesktop;
  const mobileRaw = cfg.initialSlide;
  if (forDesktop) {
    if (desktopRaw != null && desktopRaw !== '') {
      const d = Number(desktopRaw);
      if (Number.isFinite(d)) return Math.max(0, Math.trunc(d));
    }
    if (mobileRaw != null && mobileRaw !== '') {
      const fallback = Number(mobileRaw);
      if (Number.isFinite(fallback)) return Math.max(0, Math.trunc(fallback));
    }
    return cfg.centeredSlidesDesktop ? 1 : 0;
  }
  if (mobileRaw != null && mobileRaw !== '') {
    const m = Number(mobileRaw);
    if (Number.isFinite(m)) return Math.max(0, Math.trunc(m));
  }
  return cfg.centeredSlidesMobile ? 1 : 0;
}

/**
 * @param {Record<string, unknown>} raw
 */
function buildSliderConfig(raw) {
  const merged = { ...SLIDER_CONFIG_DEFAULTS, ...raw };
  const thumbs = merged.thumbsSelector;
  merged.thumbsSelector = thumbs == null || thumbs === '' ? '' : String(thumbs).trim();
  merged.sectionId = merged.sectionId == null ? '' : String(merged.sectionId);
  merged.slidesPerViewMobile = String(merged.slidesPerViewMobile ?? '1.5');
  merged.slidesPerViewTablet = merged.slidesPerViewTablet == null ? '' : String(merged.slidesPerViewTablet);
  merged.slidesPerViewDesktop = String(merged.slidesPerViewDesktop ?? '3');
  merged.direction = String(merged.direction || 'horizontal');
  merged.paginationType = String(merged.paginationType || 'bullets');
  const spaceBetweenMobileNum = Number(merged.spaceBetweenMobile);
  merged.spaceBetweenMobile = Number.isFinite(spaceBetweenMobileNum) ? spaceBetweenMobileNum : 15;
  const spaceBetweenDesktopNum = Number(merged.spaceBetweenDesktop);
  merged.spaceBetweenDesktop = Number.isFinite(spaceBetweenDesktopNum) ? spaceBetweenDesktopNum : 15;
  merged.speed = Number(merged.speed) || 500;
  merged.autoplaySpeedMobile = Number(merged.autoplaySpeedMobile) || 5000;
  merged.autoplaySpeedDesktop = Number(merged.autoplaySpeedDesktop) || 5000;
  merged.enabled = Boolean(merged.enabled);
  merged.desktopOnly = Boolean(merged.desktopOnly);
  merged.loop = Boolean(merged.loop);
  merged.zoom = Boolean(merged.zoom);
  merged.centeredSlidesMobile = Boolean(merged.centeredSlidesMobile);
  merged.centeredSlidesDesktop = Boolean(merged.centeredSlidesDesktop);
  merged.allowTouchMoveDesktop = Boolean(merged.allowTouchMoveDesktop);
  merged.slideToClickedSlide = Boolean(merged.slideToClickedSlide);
  merged.centeredSlidesBounds = Boolean(merged.centeredSlidesBounds);
  merged.showPagination = Boolean(merged.showPagination);
  merged.showNavigation = Boolean(merged.showNavigation);
  merged.showScrollbar = Boolean(merged.showScrollbar);
  merged.autoplayMobile = Boolean(merged.autoplayMobile);
  merged.autoplayDesktop = Boolean(merged.autoplayDesktop);
  merged.disableDesktop = Boolean(merged.disableDesktop);
  merged.enablePositionControls = Boolean(merged.enablePositionControls);
  merged.watchOverflow = Boolean(merged.watchOverflow);
  merged.disableTouchDragDesktop = Boolean(merged.disableTouchDragDesktop);
  const initSlide = merged.initialSlide;
  merged.initialSlide = initSlide == null || initSlide === '' ? null : Number(initSlide);
  if (!Number.isFinite(merged.initialSlide)) merged.initialSlide = null;
  const initSlideDesk = merged.initialSlideDesktop;
  merged.initialSlideDesktop = initSlideDesk == null || initSlideDesk === '' ? null : Number(initSlideDesk);
  if (!Number.isFinite(merged.initialSlideDesktop)) merged.initialSlideDesktop = null;
  merged.allowTouchMoveOverride =
    raw != null && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, 'allowTouchMove')
      ? Boolean(raw.allowTouchMove)
      : null;
  return merged;
}

/**
 * Config from `<script type="application/json" data-slider-config>` (camelCase keys).
 * Slider options are JSON-only (no `data-slider` / `data-section-id` on the host).
 */
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.componentId = this.id;
    this.paginationIcon = this.querySelector('.swiper-pagination-icon .icon');
    this._onSectionLoad = this.init.bind(this);
    this._goToFirstSlide = () => {
      this.slider?.slideTo(0);
    };
    this._thumbsPartnerRetry = 0;
    /** @type {MediaQueryList | null} */
    this._desktopOnlyMql = null;
    /** @type {(() => void) | null} */
    this._onDesktopOnlyMqlBound = null;
    this.init();
    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  /**
   * @returns {Record<string, unknown> | null}
   */
  _readSliderConfigRaw() {
    const el = this.querySelector('script[type="application/json"][data-slider-config]');
    if (!el?.textContent?.trim()) return null;
    try {
      const parsed = JSON.parse(el.textContent.trim());
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  _getSliderConfig() {
    return buildSliderConfig(this._readSliderConfigRaw() || {});
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    this._removeDesktopOnlyListener();
    this.teardownDesktopOnlyStatic();
    this._destroySliderInstance();
  }

  /** Destroy Swiper and clear prev-button loop handler state. */
  _destroySliderInstance() {
    const id = this.componentId;
    const prevBtn = this.querySelector(`.swiper-prev-button-${id}`);
    prevBtn?.removeEventListener('click', this._goToFirstSlide);
    this.slider?.destroy(true, true);
    this.slider = null;
    this._thumbsPartnerRetry = 0;
  }

  /** CSS (`swiper-block--static`) shows all slides when Swiper is not mounted (desktop-only below tablet). */
  setupDesktopOnlyStatic() {
    this.classList.add('swiper-block--static');
  }

  teardownDesktopOnlyStatic() {
    this.classList.remove('swiper-block--static');
  }

  _removeDesktopOnlyListener() {
    if (this._desktopOnlyMql && this._onDesktopOnlyMqlBound) {
      this._desktopOnlyMql.removeEventListener('change', this._onDesktopOnlyMqlBound);
    }
    this._desktopOnlyMql = null;
    this._onDesktopOnlyMqlBound = null;
  }

  _ensureDesktopOnlyListener() {
    if (!this._desktopOnlyMql) {
      this._desktopOnlyMql = window.matchMedia(`(min-width: ${THEME_BP_TABLET}px)`);
    }
    if (!this._onDesktopOnlyMqlBound) {
      this._onDesktopOnlyMqlBound = () => {
        const c = this._getSliderConfig();
        if (!c.enabled || !c.desktopOnly) return;
        this._applyDesktopOnlyBreakpoint(c);
      };
      this._desktopOnlyMql.addEventListener('change', this._onDesktopOnlyMqlBound);
    }
  }

  /**
   * @param {Record<string, unknown>} cfg
   */
  _applyDesktopOnlyBreakpoint(cfg) {
    const mql = this._desktopOnlyMql;
    if (!mql) return;
    if (mql.matches) {
      this.teardownDesktopOnlyStatic();
      this.initSlider(cfg);
    } else {
      this._destroySliderInstance();
      this.setupDesktopOnlyStatic();
    }
  }

  init() {
    const cfg = this._getSliderConfig();
    this._removeDesktopOnlyListener();

    if (!cfg.enabled) {
      this.teardownDesktopOnlyStatic();
      this._destroySliderInstance();
      return;
    }

    if (!cfg.desktopOnly) {
      this.teardownDesktopOnlyStatic();
      this.initSlider(cfg);
      return;
    }

    this._ensureDesktopOnlyListener();
    this._applyDesktopOnlyBreakpoint(cfg);
  }

  /**
   * @param {string} selector
   * @returns {any | null}
   */
  _resolveThumbsPartnerInstance(selector) {
    if (!selector) return null;
    let target;
    try {
      target = document.querySelector(selector);
    } catch {
      return null;
    }
    if (!target) return null;

    const host =
      target instanceof Element && target.tagName === 'SLIDER-COMPONENT' ? target : target.closest('slider-component');
    if (!host) return null;

    const instance = /** @type {any} */ (host).slider;
    return instance && typeof instance.slideTo === 'function' ? instance : null;
  }

  /** @param {Record<string, unknown>} [cfg] Pass through on thumbs rAF retries. */
  initSlider(cfg = this._getSliderConfig()) {
    const id = this.componentId;
    const swiperEl = this.querySelector(`.swiper-${id}`);
    if (!swiperEl || typeof Swiper === 'undefined') return;

    let thumbsPartnerInstance = null;
    const thumbsSel = String(cfg.thumbsSelector ?? '').trim();
    if (thumbsSel) {
      thumbsPartnerInstance = this._resolveThumbsPartnerInstance(thumbsSel);
      if (!thumbsPartnerInstance) {
        this._thumbsPartnerRetry += 1;
        if (this._thumbsPartnerRetry < MAX_THUMBS_PARTNER_RETRIES) {
          requestAnimationFrame(() => this.initSlider(cfg));
          return;
        }
        this._thumbsPartnerRetry = 0;
      } else {
        this._thumbsPartnerRetry = 0;
      }
    } else {
      this._thumbsPartnerRetry = 0;
    }

    this._destroySliderInstance();

    const paginationEl = this.querySelector(`.swiper-pagination-${id}`);
    const nextEl = this.querySelector(`.swiper-next-button-${id}`);
    const prevEl = this.querySelector(`.swiper-prev-button-${id}`);
    const scrollbarEl = this.querySelector(`.swiper-scrollbar-${id}`);
    const paginationType = cfg.paginationType || 'bullets';
    const showPagination = cfg.showPagination && paginationType !== 'none';

    const pagination =
      showPagination && paginationEl
        ? {
            el: paginationEl,
            clickable: true,
            type: paginationType,
            /** @param {number} _index @param {string} className */
            renderBullet(_index, className) {
              return `
            <span class="${className}">
              <span class="swiper-pagination-bullet-fill"></span>
            </span>
          `;
            },
            /** @param {string} progressbarFillClass */
            renderProgressbar(progressbarFillClass) {
              return `<span class="${progressbarFillClass}"></span>`;
            },
            /** @param {string} currentClass @param {string} totalClass */
            renderFraction(currentClass, totalClass) {
              return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`;
            },
          }
        : false;

    const navigation = cfg.showNavigation && nextEl && prevEl ? { nextEl, prevEl } : false;
    const scrollbar = cfg.showScrollbar && scrollbarEl ? { el: scrollbarEl, draggable: true } : false;
    const thumbs = thumbsPartnerInstance ? { swiper: thumbsPartnerInstance } : undefined;

    const initialSlideMobile = pickInitialSlideIndex(cfg, false);
    const initialSlideDesktop = pickInitialSlideIndex(cfg, true);

    const allowTouchMoveResolved =
      cfg.allowTouchMoveOverride !== null
        ? cfg.allowTouchMoveOverride
        : isTouchCapable()
          ? true
          : cfg.allowTouchMoveDesktop;

    const allowTouchMoveAtDesktop = cfg.disableTouchDragDesktop === true ? false : allowTouchMoveResolved;

    this.slider = new Swiper(swiperEl, {
      observer: true,
      observeParents: true,
      slidesPerView: cfg.slidesPerViewMobile,
      direction: cfg.direction || 'horizontal',
      spaceBetween: cfg.spaceBetweenMobile,
      loop: cfg.loop,
      speed: cfg.speed,
      watchOverflow: cfg.watchOverflow,
      zoom: cfg.zoom,
      centeredSlides: cfg.centeredSlidesMobile,
      initialSlide: initialSlideMobile,
      allowTouchMove: allowTouchMoveResolved,
      slideToClickedSlide: cfg.slideToClickedSlide,
      centeredSlidesBounds: cfg.centeredSlidesBounds,
      ...(thumbs != null ? { thumbs } : {}),
      pagination,
      navigation,
      scrollbar,
      mousewheel: this.classList.contains('thumbnails'),
      breakpoints: {
        [THEME_BP_TABLET]: {
          slidesPerView: cfg.slidesPerViewTablet !== '' ? cfg.slidesPerViewTablet : cfg.slidesPerViewDesktop,
          centeredSlides: cfg.centeredSlidesMobile,
          allowTouchMove: allowTouchMoveResolved,
        },
        [THEME_BP_DESKTOP]: {
          slidesPerView: cfg.slidesPerViewDesktop,
          spaceBetween: cfg.spaceBetweenDesktop,
          centeredSlides: cfg.centeredSlidesDesktop,
          initialSlide: initialSlideDesktop,
          allowTouchMove: allowTouchMoveAtDesktop,
          autoplay: cfg.autoplayDesktop
            ? {
                delay: cfg.autoplaySpeedDesktop,
                disableOnInteraction: true,
                pauseOnMouseEnter: true,
              }
            : false,
          enabled: !cfg.disableDesktop,
        },
      },
      autoplay: cfg.autoplayMobile
        ? {
            delay: cfg.autoplaySpeedMobile,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }
        : false,
    });

    const swiper = this.slider;

    if (swiper) {
      swiper.on('slideChangeTransitionStart', () => {
        document.body.classList.add('selection--transparent');
      });

      swiper.on('slideChangeTransitionEnd', () => {
        setTimeout(() => {
          document.body.classList.remove('selection--transparent');
        }, 1000);
      });

      swiper.on('reachEnd', () => {
        document.body.classList.add('selection--transparent');
        const prevBtn = this.querySelector(`.swiper-prev-button-${id}`);
        if (!prevBtn) return;
        prevBtn.removeEventListener('click', this._goToFirstSlide);
        prevBtn.addEventListener('click', this._goToFirstSlide);
      });

      swiper.on('reachBeginning', () => {
        document.body.classList.add('selection--transparent');
        const prevBtn = this.querySelector(`.swiper-prev-button-${id}`);
        prevBtn?.removeEventListener('click', this._goToFirstSlide);
      });
    }

    if (!cfg.enablePositionControls) return;

    const slidesPerView = Math.floor(parseFloat(String(this.slider?.params?.slidesPerView)));
    const firstSlide = this.slider?.slides?.length > 0 ? this.slider.slides[0] : undefined;
    if (!firstSlide || !this.slider?.params) return;

    const slideWidth = firstSlide.offsetWidth + parseIntData(String(this.slider.params.spaceBetween), 15);
    const sliderHost = document.getElementById(this.componentId);
    if (!sliderHost) return;

    const sid = cfg.sectionId;
    const paginationGroup = sid ? document.querySelector(`.swiper-pagination__group-${sid}`) : null;
    const sliderPaddingLeft = parseFloat(getComputedStyle(sliderHost).paddingLeft.replace('px', ''));
    const cardInner = firstSlide.querySelector('.card__inner') || sliderHost;
    let nextBtnPosition =
      slideWidth * slidesPerView - parseIntData(String(this.slider.params.spaceBetween), 15) - sliderPaddingLeft;
    if (sliderHost.classList.contains('swiper-video-list')) {
      nextBtnPosition = slideWidth * slidesPerView - parseIntData(String(this.slider.params.spaceBetween), 15) - 30;
    }

    const nextBtn = this.querySelector(`.swiper-next-button-${this.componentId}`);
    const nextBtnHeight = nextBtn instanceof HTMLElement ? nextBtn.offsetHeight : 0;
    const cardInnerHeight = (cardInner instanceof HTMLElement ? cardInner.offsetHeight : 0) - nextBtnHeight;
    if (nextBtn instanceof HTMLElement && swiper && Math.floor(parseFloat(String(swiper.params.slidesPerView))) !== 1) {
      nextBtn.style.left = `${nextBtnPosition}px`;
    }
    if (paginationGroup instanceof HTMLElement) {
      paginationGroup.style.top = `${cardInnerHeight / 2}px`;
    }
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}
